import firebase from "firebase/compat/app";
import { Validator } from "@pineapplelab/validators";
import { camelToSnakeCase, isArray, isNotEmptyString, isNullOrUndefined, isObject, isValidNumber, snakeToCamelCase } from "@pineapplelab/util";
import { AppErrorStateMatcher } from "./errorStateMatcher";
import { DocumentReference as ModularDocumentReference, Timestamp } from "firebase/firestore";

export abstract class Base implements Base.IBase {

  __errorsByField: { [field: string]: string[] } = {};
  __errorByField: { [field: string]: string } = {};
  __errorByFieldByDecorator: { [field: string]: { [decorator: string]: string } } = {};
  __matcherByField: { [field: string]: AppErrorStateMatcher } = {};
  __errors: string[] = [];
  __hasError: boolean = false;

  /**
   *
   * All properties that start with '__' wont be mapped to the entity on save they will be removed!
   *
   * Default format is snake_case.
   *
  */
  toJson(camelCase = false, onlyFields?: string[], onlyFieldsAreRemovedInTheJson?: boolean) {
    if (camelCase) {
      return this.baseToJson();
    }
    return this.toSnakeCaseJson();
  }

  fromJson(json: { [key: string]: any }) {
    this.fromJsonToBase(json);
    return this;
  }

  private baseToJson() {
    const trueValues: any[] = [];
    const text =  JSON.stringify( this, ( (key, value) => {
      if (this.isSensitiveValue( value )) {
        return `$__sensitiveValue${trueValues.push( value ) - 1}`;
      }
	  if ((((this as any)[key] instanceof firebase.firestore.Timestamp) || ((this as any)[key] instanceof Timestamp)) && value) {
		return { __timestamp: true, seconds: value.seconds, nanoseconds: value.nanoseconds };
	  }
      return value;
    } ) );
    const json = JSON.parse( text, ( key, value ) => {
      if (isNotEmptyString( value ) && value.startsWith('$__sensitiveValue')) {
        return trueValues[ parseInt( value.slice(17), 10 ) ];
      }
	  if (value?.__timestamp && !!value) {
		return new firebase.firestore.Timestamp(value.seconds, value.nanoseconds);
	  }
      return value;
    } );
    this.removeAllPrivetProperties( json );
    // here reverse some firebase native classes
    return json;
  }

  private toSnakeCaseJson() {
    const json = this.baseToJson();
    this.toSnakeCaseAllSubObjects( json );
    return json;
  }

  private fromJsonToBase( json: { [key: string]: any } ) {
    this.toCamelCaseAllSubObjects( json );
    Object.assign( this, json );
  }

  private toCamelCaseAllSubObjects(object: any) {
    this.modifyEveryKeyInAObject(object, snakeToCamelCase );
  }

  private toSnakeCaseAllSubObjects( object: any ) {
    this.modifyEveryKeyInAObject(object, camelToSnakeCase );
  }

  private modifyEveryKeyInAObject(object: any, keyModifier: (key: string) => string ) {
    this.doItForEveryKeyInAObject( object, (key, _object) => {
      let snakeKey = keyModifier( key );
	  // TODO: fix later, nested objects in subclasses are setted as "_" instead of "__" when calling fromJson()
	  if (snakeKey && snakeKey.startsWith('_') && !snakeKey.startsWith('__')) {
		snakeKey = `_${snakeKey}`;
	  }
      if (snakeKey === key) {
        return ;
      }
      _object[ snakeKey ] = _object[key];
      delete _object[key];
    });
  }

  private removeAllPrivetProperties(object: any) {
    this.doItForEveryKeyInAObject(object, (key, _object) => {
      if (key.startsWith('__')) {
        delete _object[key];
      }
    });
  }

  private doItForEveryKeyInAObject(object: any, it: (key: string, object: any) => void, deepLevel = 0 ) {
    deepLevel++;
    if (deepLevel > DEEP_LEVEL_MAX || !isObject(object) || this.isSensitiveValue( object ) ) {
      return ;
    }
    if (isArray(object as any[])) {
      return object.map( (item: any) => this.doItForEveryKeyInAObject(item, it, deepLevel ) );
    }
    Object.keys( object ).map( key => {
      if (isObject( object[key] )) {
        this.doItForEveryKeyInAObject( object[key], it, deepLevel );
      }
      it(key, object);
    } );
  }

  private isSensitiveValue( object: any ) {
    if (isNullOrUndefined( object )) {
      return false;
    }
    if (object instanceof ModularDocumentReference) {
      return true;
    }
    const types = [firebase.firestore.DocumentReference, firebase.firestore.FieldValue, AppErrorStateMatcher];
    return types.some( type => object instanceof type );
  }

  isValid() {
    const result = Validator.validate( this );
    this.__hasError = !result.valid;
    this.__errorByField = result.fieldError;
    this.__errorsByField = result.fieldErrors;
    this.__errors = result.errors;
    this.__errorByFieldByDecorator = result.fieldDecoratorError;
    Object.keys( this.__errorByField ).map( field => {
      if (isNullOrUndefined( this.__matcherByField[field] )) {
        this.__matcherByField[field] = new AppErrorStateMatcher( this, field );
      }
    });
    return !this.__hasError;
  }

}

export namespace Base {

  export interface IModelRelationship {
    id: string;
    reference: firebase.firestore.DocumentReference;
    name: string;
  };

  export interface IBase {
    __matcherByField: { [field: string]: AppErrorStateMatcher };
    __errorByField: { [field: string]: string };
    isValid: () => boolean;
    [key: string]: any;
  };

}

const DEEP_LEVEL_MAX = 5;
